import { memo, useMemo, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface LottieLoadingProps {
  /** Optional message to display below the animation */
  message?: string;
  /** Size of the animation in pixels (default: 180px) */
  size?: number;
  /** Additional CSS classes for the container */
  className?: string;
  /** Whether to display in full-viewport mode (default: true) */
  fullScreen?: boolean;
}

// Stable animation source to prevent unnecessary re-renders
const ANIMATION_SRC = '/assets/charging-loading.lottie';

// Simple fallback spinner component
const FallbackSpinner = memo(function FallbackSpinner({ size }: { size: number }) {
  return (
    <div className="flex items-center justify-center" style={{ width: size, height: size }}>
      <Loader2
        className="animate-spin text-[#8FB560]"
        style={{ width: size * 0.4, height: size * 0.4 }}
      />
    </div>
  );
});

// Memoized animation component to prevent re-renders when message changes
const LottieAnimation = memo(function LottieAnimation({ size }: { size: number }) {
  const [hasError, setHasError] = useState(false);

  // Memoize prefers-reduced-motion check to prevent recalculation
  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  // Memoize animation style object to ensure stable reference for DotLottieReact
  const animationStyle = useMemo(
    () => ({ width: size, height: size, margin: '0 auto' as const }),
    [size]
  );

  // Use fallback spinner if Lottie fails to load or user prefers reduced motion
  if (prefersReducedMotion || hasError) {
    return <FallbackSpinner size={size} />;
  }

  return (
    <DotLottieReact
      src={ANIMATION_SRC}
      loop
      autoplay
      style={animationStyle}
      dotLottieRefCallback={(dotLottie) => {
        if (dotLottie) {
          dotLottie.addEventListener('loadError', () => {
            setHasError(true);
          });
        }
      }}
    />
  );
});

/**
 * Standardized loading component using the Hollywood Trucks Lottie animation.
 * Use this for full-width/viewport loading states.
 * For inline spinners, continue using Loader2 or CSS spinners.
 */
export function LottieLoading({
  message,
  size = 180,
  className,
  fullScreen = true,
}: LottieLoadingProps) {
  // Memoize animation so it never re-mounts when message changes
  const animation = useMemo(() => <LottieAnimation size={size} />, [size]);

  const content = (
    <div className="text-center" role="status" aria-live="polite" aria-label={message || 'Loading'}>
      {animation}
      {message && <p className="text-sm text-muted-foreground -mt-4">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div className={cn('flex items-center justify-center min-h-screen bg-background', className)}>
        {content}
      </div>
    );
  }

  return <div className={cn('flex items-center justify-center', className)}>{content}</div>;
}
